import type {ActionReducerMapBuilder} from '@reduxjs/toolkit';
import {
  fetchCampaignRecipientInsights,
  fetchCampaignRecipientInsightsTotalRows,
  fetchMailingListRecipientInsights,
  fetchMailingListRecipientInsightsTotalRows,
  fetchNegativeOutcomeCounts,
  initEmailMarketingCampaignPerformance,
  removeEmailsForCampaign,
  removeEmailsForMailingList,
} from './email-marketing-performance-thunk';
import {LOADING_STATES} from '@Libraries/email-marketing-campaigns-library';
import {getDefaultRecipientInsightsPaging, getFirstSelectedItemId} from './email-marketing-campaign-performance-library';
import type {
  EmailMarketingCampaignPerformanceState,
  FetchNegativeOutcomeCountsArg,
  DetailsPageResponsePaging,
  RecipientInsightsPaging,
  NegativeOutcomeRecipientCounts,
} from './email-marketing-campaign-performance.types';

function setPerformanceLoading(state: EmailMarketingCampaignPerformanceState): void {
  state.performanceLoadingState = LOADING_STATES.LOADING;
}

function setPerformanceLoaded(state: EmailMarketingCampaignPerformanceState): void {
  state.performanceLoadingState = LOADING_STATES.LOADED;
}

function resetRecipientInsightsPagingState(state: EmailMarketingCampaignPerformanceState): void {
  state.detailsLoadingState = LOADING_STATES.NOT_LOADED;
  state.detailsTotalRows = null;
  state.detailsRequestedPaging = getDefaultRecipientInsightsPaging();
  state.detailsLoadedRowsPaging = getDefaultRecipientInsightsPaging();
  state.detailsLatestRequestId = '';
}

function isStaleRecipientInsightsResponse(state: EmailMarketingCampaignPerformanceState, requestId: string, selectedItemId: string): boolean {
  return requestId !== state.detailsLatestRequestId || selectedItemId !== getFirstSelectedItemId(state.selectedItemIds);
}

function setRequestedDetailsPagingInState(state: EmailMarketingCampaignPerformanceState, requestId: string, paging: RecipientInsightsPaging): void {
  state.detailsLoadingState = LOADING_STATES.LOADING;
  state.detailsRequestedPaging = {
    offset: paging.offset,
    limit: paging.limit,
    sortField: paging.sortField,
    sortDir: paging.sortDir,
  };
  state.detailsLatestRequestId = requestId;
}

function setLoadedDetailsPagingInState(
  state: EmailMarketingCampaignPerformanceState,
  payload: DetailsPageResponsePaging,
  sortField: RecipientInsightsPaging['sortField'],
  sortDir: RecipientInsightsPaging['sortDir']
): void {
  state.detailsLoadingState = LOADING_STATES.LOADED;
  const fetchedPaging: RecipientInsightsPaging = {
    offset: payload.offset,
    limit: payload.limit,
    sortField,
    sortDir,
  };
  state.detailsRequestedPaging = fetchedPaging;
  state.detailsLoadedRowsPaging = {...fetchedPaging};
}

function getItemIdFromNegativeOutcomeCountsArg(arg: FetchNegativeOutcomeCountsArg): string {
  return 'campaignId' in arg ? arg.campaignId : arg.mailingListId;
}

function setNegativeOutcomeCountsInState(state: EmailMarketingCampaignPerformanceState, itemId: string, counts: NegativeOutcomeRecipientCounts): void {
  state.negativeOutcomeCounts[itemId] = counts;
}

function setZeroNegativeOutcomeCountsInStateWhenNoneFetched(state: EmailMarketingCampaignPerformanceState, arg: FetchNegativeOutcomeCountsArg): void {
  if (!('mailingListId' in arg) || state.negativeOutcomeCounts[arg.mailingListId]) {
    return;
  }
  setNegativeOutcomeCountsInState(state, arg.mailingListId, {numInvalidRecipients: 0, numSpamOrComplaintRecipients: 0, numUnsubscribedRecipients: 0});
}

function restoreLoadedRecipientInsightsPagingInState(state: EmailMarketingCampaignPerformanceState): void {
  state.detailsLoadingState = LOADING_STATES.LOADED;
  state.detailsRequestedPaging = {...state.detailsLoadedRowsPaging};
}

export const emailMarketingCampaignPerformanceExtraReducers = (builder: ActionReducerMapBuilder<EmailMarketingCampaignPerformanceState>): void => {
  builder.addCase(initEmailMarketingCampaignPerformance.pending, (state, {meta}) => {
    state.performanceLoadingState = LOADING_STATES.LOADING;
    state.latestInitRequestId = meta.requestId;
  });

  builder.addCase(initEmailMarketingCampaignPerformance.fulfilled, (state, {payload, meta}) => {
    if (meta.requestId !== state.latestInitRequestId) {
      return;
    }
    state.campaigns = payload.campaigns ?? {};
    state.campaignsWithMetricsAvailable = payload.campaignsWithMetricsAvailable ?? {};
    state.campaignsWithMetricsNotAvailable = payload.campaignsWithMetricsNotAvailable ?? {};
    state.campaignsWithMetricsCollecting = payload.campaignsWithMetricsCollecting ?? {};
    state.campaignMetrics = payload.campaignMetrics ?? {};
    state.mailingLists = payload.mailingLists ?? {};
    state.mailingListsWithAvailableMetrics = payload.mailingListsWithAvailableMetrics ?? {};
    state.mailingListsWithMetricsNotAvailable = payload.mailingListsWithMetricsNotAvailable ?? {};
    state.mailingListsWithMetricsCollecting = payload.mailingListsWithMetricsCollecting ?? {};
    state.mailingListMetrics = payload.mailingListMetrics ?? {};
    state.itemType = payload.itemType;
    state.selectedItemIds = payload.selectedItemIds.map((id) => String(id));
    state.isNoCampaignCreated = payload.isNoCampaignCreated ?? false;
    state.isNoCampaignPublished = payload.isNoCampaignPublished ?? false;
    state.isCollectingData = payload.isCollectingData;
    state.isNoMailingListCreated = payload.isNoMailingListCreated ?? false;
    state.isNotCollectingData = payload.isNotCollectingData ?? false;
    state.performanceLoadingState = LOADING_STATES.LOADED;
    state.negativeOutcomeCounts = {};
    resetRecipientInsightsPagingState(state);
  });

  builder.addCase(fetchNegativeOutcomeCounts.fulfilled, (state, {payload, meta}) => {
    setNegativeOutcomeCountsInState(state, getItemIdFromNegativeOutcomeCountsArg(meta.arg), payload);
  });

  builder.addCase(fetchNegativeOutcomeCounts.rejected, (state, {meta}) => {
    setZeroNegativeOutcomeCountsInStateWhenNoneFetched(state, meta.arg);
  });

  builder.addCase(fetchMailingListRecipientInsightsTotalRows.fulfilled, (state, {payload, meta}) => {
    if (meta.arg.mailingListId !== getFirstSelectedItemId(state.selectedItemIds)) {
      return;
    }
    state.detailsTotalRows = payload.totalRows;
  });

  builder.addCase(fetchCampaignRecipientInsightsTotalRows.fulfilled, (state, {payload, meta}) => {
    if (meta.arg.campaignId !== getFirstSelectedItemId(state.selectedItemIds)) {
      return;
    }
    state.detailsTotalRows = payload.totalRows;
  });

  builder.addCase(fetchCampaignRecipientInsights.pending, (state, {meta}) => {
    setRequestedDetailsPagingInState(state, meta.requestId, meta.arg);
  });

  builder.addCase(fetchCampaignRecipientInsights.fulfilled, (state, {payload, meta}) => {
    if (isStaleRecipientInsightsResponse(state, meta.requestId, meta.arg.campaignId)) {
      return;
    }
    setLoadedDetailsPagingInState(state, payload, meta.arg.sortField, meta.arg.sortDir);

    const metricsForCampaign = state.campaignMetrics[meta.arg.campaignId];
    if (!metricsForCampaign) {
      return;
    }
    metricsForCampaign.details = payload.details;
  });

  builder.addCase(fetchCampaignRecipientInsights.rejected, (state, {meta}) => {
    if (isStaleRecipientInsightsResponse(state, meta.requestId, meta.arg.campaignId)) {
      return;
    }
    restoreLoadedRecipientInsightsPagingInState(state);
  });

  builder.addCase(fetchMailingListRecipientInsights.pending, (state, {meta}) => {
    setRequestedDetailsPagingInState(state, meta.requestId, meta.arg);
  });

  builder.addCase(fetchMailingListRecipientInsights.fulfilled, (state, {payload, meta}) => {
    if (isStaleRecipientInsightsResponse(state, meta.requestId, meta.arg.mailingListId)) {
      return;
    }
    setLoadedDetailsPagingInState(state, payload, meta.arg.sortField, meta.arg.sortDir);

    const metricsForMailingList = state.mailingListMetrics[meta.arg.mailingListId];
    if (!metricsForMailingList) {
      return;
    }
    metricsForMailingList.insights = payload.details;
  });

  builder.addCase(fetchMailingListRecipientInsights.rejected, (state, {meta}) => {
    if (isStaleRecipientInsightsResponse(state, meta.requestId, meta.arg.mailingListId)) {
      return;
    }
    restoreLoadedRecipientInsightsPagingInState(state);
  });

  builder.addCase(removeEmailsForCampaign.pending, setPerformanceLoading);

  builder.addCase(removeEmailsForCampaign.rejected, setPerformanceLoaded);

  builder.addCase(removeEmailsForCampaign.fulfilled, (state, {meta}) => {
    setPerformanceLoaded(state);
    const metricsForCampaign = state.campaignMetrics[meta.arg.id];
    if (!metricsForCampaign) {
      return;
    }
    meta.arg.emails.forEach((email) => {
      const detailsRow = metricsForCampaign.details[email];
      if (detailsRow) {
        detailsRow.isDeleted = true;
      }
    });
  });

  builder.addCase(removeEmailsForMailingList.pending, setPerformanceLoading);

  builder.addCase(removeEmailsForMailingList.rejected, setPerformanceLoaded);

  builder.addCase(removeEmailsForMailingList.fulfilled, (state, {meta}) => {
    setPerformanceLoaded(state);
    const metricsForMailingList = state.mailingListMetrics[meta.arg.id];
    if (!metricsForMailingList) {
      return;
    }
    meta.arg.emails.forEach((email) => {
      const insightsRow = metricsForMailingList.insights[email];
      if (insightsRow) {
        insightsRow.isDeleted = true;
      }
    });
  });
};
